import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { NeighborContext } from '../../../api/app-types/neighbors.app';
import { Sponsor, SponsorExtended, SponsorPerson } from '../../../api/app-types/sponsor.app';
import { SPONSOR_SERVICE } from '../../../api/services/sponsor.service';
import { PaginatedResponse } from '../../pagination/paginated-response';
import { LocalStorageService } from '../../storage/local-storage.service';

const resolveInitialPageSize = (
	storage: LocalStorageService,
	auth: MsalService | null,
	preferenceKey: string,
	defaultPageSize: number,
	pageSizeOptions: number[],
) => {
	const userId = storage.resolveCurrentUserId(auth);
	if (!userId) return defaultPageSize;

	const saved = storage.getPageSize(userId, preferenceKey);
	return saved && pageSizeOptions.includes(saved) ? saved : defaultPageSize;
};

export const sponsorResolver: ResolveFn<PaginatedResponse<Sponsor>> = async (
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(SPONSOR_SERVICE);
	const response = await api.getAllSponsors();
	return response;
};

export const sponsorByIdResolver: ResolveFn<SponsorExtended> = async (
	route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(SPONSOR_SERVICE);
	const id = Number(route.paramMap.get('id')!);
	return api.getById(id);
};

export const sponsorPersonsResolver: ResolveFn<PaginatedResponse<SponsorPerson>> = async (
	route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(SPONSOR_SERVICE);
	const env = inject(ENVIRONMENT);
	const storage = inject(LocalStorageService);
	const auth = inject(MsalService, { optional: true });
	const id = Number(route.paramMap.get('id')!);

	const offset = Number(route.queryParamMap.get('offset') ?? 0);
	const rangeFromQuery = route.queryParamMap.get('range');
	const range = Number(
		rangeFromQuery ??
			resolveInitialPageSize(storage, auth, 'sponsor-persons-list', env.defaultPageSize, env.pageSizeOptions),
	);

	return api.getPersons(id, offset, range);
};

export const sponsorPersonResolver: ResolveFn<SponsorPerson> = async (
	route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(SPONSOR_SERVICE);
	const sponsorId = Number(route.paramMap.get('id')!);
	const personId = Number(route.paramMap.get('personId')!);
	return api.getPersonById(sponsorId, personId);
};

export const sponsorPersonNeighborsResolver: ResolveFn<NeighborContext<SponsorPerson>> = async (
	route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(SPONSOR_SERVICE);
	const sponsorId = route.paramMap.get('id')!;
	const personId = route.paramMap.get('personId')!;
	return api.getPersonNeighbors(sponsorId, personId);
};
